import { ListBox } from '@repo/foundations';
import { isNil } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { FILTER_OPTIONS_DIVIDER } from '../constants';
import { FilterMenuItem } from '../FilterMenuItem';
import type {
	FilterDropdownConfigNestedList,
	FilterItem,
	FilterOperator,
	FilterValue,
	FilterValueType,
} from '../types';
import { FilterType } from '../types';
import { useFilterDropdownNestedList } from './useFilterDropdownNestedList';

export interface FilterDropdownNestedListProps {
	value: FilterValue;
	getItems: FilterDropdownConfigNestedList['getItems'];
	filterType?: FilterType;
	renderMenuItem?: (item: FilterItem) => React.ReactNode;
	onChange: (value: FilterValue, close: boolean) => void;
	operator: FilterOperator;
}

export function FilterDropdownNestedList({
	getItems,
	value,
	filterType = FilterType.Single,
	renderMenuItem,
	onChange,
	operator,
}: FilterDropdownNestedListProps) {
	const { selected, unselected } = useFilterDropdownNestedList({
		getItems,
		value,
		operator,
	});

	const onSelect = useCallback(
		(item: FilterItem) => (close: boolean) => {
			if (filterType === FilterType.Single) {
				onChange(
					{
						...value,
						value: item.value,
						subValue: undefined,
					},
					close
				);
			} else if (filterType === FilterType.Multiple) {
				let currentValue: FilterValueType[] = [];

				if (!isNil(value.value)) {
					currentValue = Array.isArray(value.value)
						? value.value
						: [value.value];
				}

				onChange(
					{
						...value,
						value: [...currentValue, item.value],
						subValue: undefined,
					},
					close
				);
			} else {
				throw new Error('Invalid filter type');
			}
		},
		[filterType, onChange, value]
	);

	const onDeselect = useCallback(
		(item: FilterItem) => (close: boolean) => {
			if (filterType === FilterType.Single) {
				return;
			} else if (filterType === FilterType.Multiple) {
				let currentValue: FilterValueType[] = [];

				if (!isNil(value.value)) {
					currentValue = Array.isArray(value.value)
						? value.value
						: [value.value];
				}

				const newValue = currentValue.filter(
					(currentValueItem) => currentValueItem !== item.value
				);

				onChange(
					{
						...value,
						value: newValue,
						subValue: undefined,
					},
					close
				);
			} else {
				throw new Error('Invalid filter type');
			}
		},
		[filterType, onChange, value]
	);

	const items = useMemo(() => {
		const result: Array<FilterItem | typeof FILTER_OPTIONS_DIVIDER> = [
			...selected,
		];

		if (selected.length > 0 && unselected.length) {
			result.push(FILTER_OPTIONS_DIVIDER);
		}

		result.push(...unselected);

		return result;
	}, [selected, unselected]);

	return (
		<ListBox.ItemsDropdown
			items={items}
			renderItem={(item, getProps, idx) => {
				if (item === FILTER_OPTIONS_DIVIDER) {
					return <ListBox.Divider key={`divider-${idx}`} />;
				}

				const isSelected = selected.includes(item);

				return (
					<FilterMenuItem
						key={`filter-value-${item.value}`}
						filterType={filterType}
						item={item}
						renderMenuItem={renderMenuItem}
						onClick={isSelected ? onDeselect(item) : onSelect(item)}
						selected={isSelected ? true : undefined}
						getProps={getProps}
					/>
				);
			}}
		/>
	);
}
